/**
 * Retains the shopper's reading position only while Shopify's native privacy
 * controls dismiss after a consent choice. It never prevents that choice,
 * moves focus, or reads/writes consent state.
 */
(() => {
  'use strict';

  const CHOICE_IDS = new Set([
    'shopify-pc__banner__btn-accept',
    'shopify-pc__banner__btn-decline',
    'shopify-pc__prefs__header-accept',
    'shopify-pc__prefs__header-decline',
    'shopify-pc__prefs__header-save'
  ]);
  const SURFACE_SELECTORS = '#shopify-pc__banner, #shopify-pc__prefs__dialog';
  const MAX_WAIT_MS = 1800;
  const POSITION_TOLERANCE_PX = 2;
  let pending = null;
  let dismissalCheckQueued = false;

  const choiceButton = (target) => {
    if (!(target instanceof Element)) return null;
    const button = target.closest('button, input[type="submit"], input[type="button"]');
    return button && CHOICE_IDS.has(button.id) ? button : null;
  };
  const surfaceIsVisible = (surface) => {
    if (!surface || !surface.isConnected || !surface.getClientRects().length || surface.hidden || surface.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(surface);
    return style.display !== 'none' && style.visibility !== 'hidden';
  };
  const consentSurfacesClosed = () => !Array.from(document.querySelectorAll(SURFACE_SELECTORS)).some(surfaceIsVisible);
  const clearPending = () => {
    if (!pending) return;
    window.clearTimeout(pending.expiryTimer);
    pending.observer.disconnect();
    pending = null;
    dismissalCheckQueued = false;
  };
  const restoreIfDismissed = () => {
    if (!pending || !consentSurfacesClosed()) return;
    const snapshot = pending;
    const displaced = Math.abs(window.scrollX - snapshot.x) > POSITION_TOLERANCE_PX
      || Math.abs(window.scrollY - snapshot.y) > POSITION_TOLERANCE_PX;
    if (!displaced) return;
    clearPending();
    const maximumY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    window.scrollTo({ left: snapshot.x, top: Math.min(snapshot.y, maximumY), behavior: 'instant' });
  };
  const queueDismissalCheck = () => {
    const snapshot = pending;
    if (!snapshot || dismissalCheckQueued) return;
    dismissalCheckQueued = true;
    // Native banner removal and the browser's focus/layout update can occur in
    // separate frames. Wait for both before measuring the viewport.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (pending !== snapshot) return;
        dismissalCheckQueued = false;
        restoreIfDismissed();
      });
    });
  };
  const refreshExpiry = () => {
    if (!pending) return;
    window.clearTimeout(pending.expiryTimer);
    pending.expiryTimer = window.setTimeout(clearPending, MAX_WAIT_MS);
  };
  const cancelForDeliberateAction = (event) => {
    if (!pending) return;
    const isInitiatingChoice = choiceButton(event.target)
      && (event.type === 'pointerdown'
        || event.type === 'click'
        || (event.type === 'keydown' && (event.key === 'Enter' || event.key === ' ')));
    if (isInitiatingChoice) return;
    clearPending();
  };
  const capturePosition = (event) => {
    const choice = choiceButton(event.target);
    if (!choice) return;
    // Pointer, keyboard, and synthetic click activation can all occur for one
    // choice. Keep the first snapshot, but start the bounded wait at the click.
    if (pending) {
      if (event.type === 'click') refreshExpiry();
      return;
    }
    const observer = new MutationObserver(queueDismissalCheck);
    pending = {
      x: window.scrollX,
      y: window.scrollY,
      observer,
      expiryTimer: 0
    };
    for (const surface of document.querySelectorAll(SURFACE_SELECTORS)) {
      observer.observe(surface, {
        attributes: true,
        attributeFilter: ['aria-hidden', 'class', 'hidden', 'style']
      });
      if (surface.parentElement) observer.observe(surface.parentElement, { childList: true });
    }
    refreshExpiry();
  };

  document.addEventListener('pointerdown', capturePosition, true);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') capturePosition(event);
  }, true);
  // Covers assistive technology and synthetic browser activation where no
  // pointerdown or activation key was emitted.
  document.addEventListener('click', capturePosition, true);
  document.addEventListener('wheel', cancelForDeliberateAction, { passive: true, capture: true });
  document.addEventListener('touchmove', cancelForDeliberateAction, { passive: true, capture: true });
  document.addEventListener('keydown', cancelForDeliberateAction, true);
  document.addEventListener('pointerdown', cancelForDeliberateAction, true);
  document.addEventListener('click', cancelForDeliberateAction, true);
  document.addEventListener('visitorConsentCollected', queueDismissalCheck);
  // Captures a delayed native focus/layout scroll while the bounded snapshot remains active.
  window.addEventListener('scroll', queueDismissalCheck, { passive: true });
})();
